import { isAsyncFunction, isHTMLString, isSVGElement } from "@/utils/is";
import type { IIConProps } from "./types";
import {
  defineComponent,
  type VNode,
  computed,
  type CSSProperties,
  ref,
  isVNode,
} from "vue";
import { elementUpdateAttribute } from "@/utils/html";
import { computedWithControl } from "@vueuse/core";
import { useMemoAsync } from "@/utils/async/useMemoAsync";
import { isFunction, isString } from "math";
import { unwrapElementRef } from "@/utils/dom/unwrap";
import { condcat } from "@/utils/string";
import { Spinner } from "../spinner";
import { getSVGTree, modifyTree } from "./utils";
import { convertIconSource } from "./utils/convert";
import type { IBaseComponentProps } from "../base/types";

const VisibilityCheck = defineComponent<IBaseComponentProps>({
  setup() {
    return () => {
      return <Spinner />;
    };
  },
});

const cache = new Map<string, any>();

const Icon = defineComponent<IIConProps>({
  setup(props) {
    const depth = computed(() => props.depth || 0);

    const transform = computed((): string =>
      condcat(
        [
          [props.flipHori === true, "scale(-1, 1)"],
          [props.flipVert === true, "scale(1, -1)"],
          [
            Math.abs(props.rotationDeg || 0) > 0.0,
            `rotate(${props.rotationDeg}deg)`,
          ],
        ],
        " ",
      ),
    );

    const flipStyle = computed((): CSSProperties => {
      return {
        ...(transform.value.length > 1 ? { transform: transform.value } : {}),
      };
    });

    const style = computed((): CSSProperties => {
      return {
        verticalAlign: "middle",
        textAlign: "center",
        ...(props.inline
          ? {
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
            }
          : {}),
        ...(props.fill && props.fill !== "original"
          ? { fill: props.fill }
          : {}),
        ...(props.stroke && props.stroke !== "original"
          ? { stroke: props.stroke }
          : {}),
        ...(props.color ? { color: props.color } : {}),
        ...(props.size
          ? {
              //    viewBox: `0 0 ${size} ${size}`
            }
          : {}),
      };
    });

    const onMounted = (node: VNode) => {
      queueMicrotask(() => {
        if (!node.el) return;
        const el = node.el as SVGElement;
        if (!el) return;
        if (!isFunction(el.getAttribute)) return;
        if (!isFunction(el.setAttribute)) return;
        elementUpdateAttribute(
          el as unknown as HTMLElement,
          "stroke",
          props.stroke || "none",
        );
        if (el.getAttribute("width") !== el.getAttribute("height")) return;
        if (props.size) {
          el.setAttribute("width", props.size + "");
          el.setAttribute("height", props.size + "");
        }
        if (props.width) {
          el.setAttribute("width", props.width + "");
        }
        if (props.height) {
          el.setAttribute("height", props.height + "");
        }
        if (
          !el.querySelector("g") ||
          !el.querySelector("defs") ||
          (el as unknown as HTMLElement).querySelectorAll("path").length < 2
        )
          return;
        const svgPath = el.querySelector("path");
        if (
          svgPath &&
          svgPath.parentElement &&
          svgPath.parentElement.parentElement
        ) {
          const upper = svgPath.parentElement?.parentElement;
          upper.innerHTML = "";
          upper.appendChild(svgPath);
        }
      });
    };

    const visCheck = ref();

    const getSource = async () => {
      let src = props.src || props.source;
      const cacheKey = props.cacheKey
        ? props.cacheKey
        : isAsyncFunction(src)
          ? src + ""
          : undefined;

      if (cacheKey) {
        const cached = cache.get(cacheKey);
        if (cached) return cached;
      }

      if (isAsyncFunction(src)) {
        src = await src();
      }
      if (src && (src as any).default) {
        src = (src as any).default;
      }
      const awaitedSrc = src ? await Promise.resolve(src) : undefined;

      if (awaitedSrc && cacheKey) {
        cache.set(cacheKey, awaitedSrc);
      }
      return awaitedSrc;
    };

    const rendered = useMemoAsync(async () => {
      const awaitedSrc = await getSource();

      const converted = await convertIconSource({
        depth,
        flipStyle,
        style,
        props,
        source: awaitedSrc,
      });
      return converted;
    }, [() => props.src, () => props.source, style, flipStyle]);

    const innerContent = computedWithControl(
      [() => rendered.data, () => rendered.loading],
      () => {
        try {
          const renderedData = rendered.data;
          if (rendered.loading && !renderedData)
            return <VisibilityCheck ref={visCheck} />;
          const result = renderedData;

          const content = result;

          if (isString(result)) {
            const s = result as string;
            if (s.startsWith("data:image/svg+xml,")) {
              const part = s.split("data:image/svg+xml,")[1];
              const contents = decodeURI(part);
              if (isHTMLString(contents)) {
                const tree = getSVGTree(contents);
                if (tree) {
                  modifyTree(tree, props, style.value);
                  return (
                    <div
                      style={[style.value, flipStyle.value]}
                      innerHTML={tree.outerHTML || tree.innerHTML}
                    />
                  );
                }
              }
            }
          }

          if (isSVGElement(result)) {
            const tree = result;
            modifyTree(tree, props, style.value);
            return (
              <div
                style={[style.value, flipStyle.value]}
                innerHTML={tree.outerHTML || tree.innerHTML}
              />
            );
          }

          if (isHTMLString(result)) {
            const tree = getSVGTree(result);
            if (!tree) return <div />;
            modifyTree(tree, props, style.value);
            return (
              <div
                style={[style.value, flipStyle.value]}
                innerHTML={tree.outerHTML || tree.innerHTML}
              />
            );
          }

          if (isVNode(content) && content.type === "svg" && content.props) {
            content.props.onVnodeMounted = (node) => {
              onMounted(node);
              const el = unwrapElementRef(node);
              if (!el || el.tagName !== "svg") return;
              const svg = el as unknown as SVGElement;
              modifyTree(svg, props, flipStyle.value);
            };
          }

          if (isVNode(content)) {
            return content;
          }

          return <VisibilityCheck ref={visCheck} />;
        } catch (e) {
          console.error(e);
          return <span />;
        }
      },
    );

    return () => {
      return innerContent.value;
    };
  },
});

Icon.props = [
  "src",
  "size",
  "flipHori",
  "flipVert",
  "fill",
  "stroke",
  "viewBox",
  "inline",
  "rotationDeg",
  "excludeFillTags",
  "excludeStrokeTags",
  "noNewAttributes",
  "color",
  "depth",
  "source",
  "pathOptions",
  "fallback",
  "cacheKey",
  "width",
  "height"
];

export default Icon;
